"use client";

import { c as _c } from "react/compiler-runtime";
import type { ImgHTMLAttributes } from "react";
import { useLibraryMediaRecord } from "../ui/hooks/useMediaLibraryAltText";
import { dynamicImageMediaId, useDynamicImageMedia } from "../ui/media/DynamicImageMediaContext";
import { resolveStaticImageField } from "./dynamicImageText";
type DynamicPreviewImageProps = ImgHTMLAttributes<HTMLImageElement> & {
  binding: "featured" | "author" | "user";
  "data-pb-node"?: string;
  elementAlt: string;
  elementTitle?: string;
};

/**
 * In-app preview only. Published HTML already has File Manager alt/title
 * written onto the node by `resolveBuilderMediaUrls`.
 */
export function DynamicPreviewImage(t0) {
  const $ = _c(8);
  const {
    binding,
    elementAlt,
    elementTitle,
    alt: _ignoredAlt,
    title: _ignoredTitle,
    ...rest
  } = t0;
  const source = useDynamicImageMedia();
  const src = typeof rest.src === "string" ? rest.src : null;
  const mediaId = dynamicImageMediaId(binding, {
    authorImageUrl: source.authorImageUrl,
    featuredImageUrl: binding === "featured" ? src : null,
    featuredMediaId: source.featuredMediaId,
    userImageUrl: source.userImageUrl
  }) ?? null;
  const library = useLibraryMediaRecord(mediaId);
  const waiting = Boolean(mediaId) && !library.ready;
  const alt = waiting ? "" : library.found ? library.alt : elementAlt;
  let t1;
  if ($[0] !== elementTitle || $[1] !== library || $[2] !== waiting) {
    t1 = waiting ? undefined : library.found ? library.title.trim() || undefined : elementTitle?.trim() || undefined;
    $[0] = elementTitle;
    $[1] = library;
    $[2] = waiting;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const title = t1;
  let t2;
  if ($[4] !== alt || $[5] !== rest || $[6] !== title) {
    t2 = <img {...rest} alt={alt} title={title} />;
    $[4] = alt;
    $[5] = rest;
    $[6] = title;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
type StaticPreviewImageProps = ImgHTMLAttributes<HTMLImageElement> & {
  elementAlt: string;
  elementTitle?: string;
  mediaId: string;
};
export function StaticPreviewImage(t0) {
  const $ = _c(17);
  let elementAlt;
  let elementTitle;
  let mediaId;
  let rest;
  if ($[0] !== t0) {
    const {
      elementAlt: t1,
      elementTitle: t2,
      mediaId: t3,
      alt: _ignoredAlt,
      title: _ignoredTitle,
      ...t4
    } = t0;
    elementAlt = t1;
    elementTitle = t2;
    mediaId = t3;
    rest = t4;
    $[0] = t0;
    $[1] = elementAlt;
    $[2] = elementTitle;
    $[3] = mediaId;
    $[4] = rest;
  } else {
    elementAlt = $[1];
    elementTitle = $[2];
    mediaId = $[3];
    rest = $[4];
  }
  const library = useLibraryMediaRecord(mediaId);
  let t1;
  if ($[5] !== library) {
    t1 = library.found ? library : {
      alt: "",
      title: ""
    };
    $[5] = library;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const file = t1;
  let t2;
  if ($[7] !== elementAlt || $[8] !== file.alt) {
    t2 = resolveStaticImageField(elementAlt, file.alt);
    $[7] = elementAlt;
    $[8] = file.alt;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const alt = t2;
  let t3;
  if ($[10] !== elementTitle || $[11] !== file.title) {
    t3 = resolveStaticImageField(elementTitle, file.title);
    $[10] = elementTitle;
    $[11] = file.title;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const title = t3;
  const t4 = title || undefined;
  let t5;
  if ($[13] !== alt || $[14] !== rest || $[15] !== t4) {
    t5 = <img {...rest} alt={alt} title={t4} />;
    $[13] = alt;
    $[14] = rest;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  return t5;
}